import "./App.css";
import { TabPage } from "./pages/TabPage";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Routes, Route, Navigate } from "react-router-dom";
import MemberForm from "./pages/memberForm/MemberForm";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import { PageLayout } from "./components/PageLayout";
import { b2cPolicies, protectedResources, features } from "./authConfig";
import { compareIssuingPolicy } from "./utils/claimUtils";
import React, { useEffect } from "react";
import { ProtectedRoute } from "./components/protectedRoute/ProtectedRoute";
import RoleAssignmentPage from "./pages/roleAssignmentPage/RoleAssignmentPage";
// import MemberGroupsPage from "./pages/memberGroupsPage/MemberGroupsPage";
import { HomePage } from "./pages/homePage/HomePage";
import { RedirectPage } from "./pages/redirectPage/RedirectPage";
import MemberGroupsPage from "./pages/memberGroupsPage/MemberGroupsPage";

const theme = createTheme({
  typography: {
    h1: {
      fontSize: "20px",
      fontWeight: "700",
      lineHeight: "24px",
    },
    h2: {
      fontSize: "16px",
      fontWeight: "500",
      lineHeight: "19px",
      marginBottom: "36px",
    },
    h6: {
      fontSize: "14px",
      fontWeight: "400",
      lineHeight: "16.8px",
    },
    subtitle1: {
      fontSize: "10px",
      fontWeight: "400",
      lineHeight: "12px",
    },
    body1: {
      fontSize: "12px",
      fontWeight: "400",
      lineHeight: "14px",
    },
    body2: {
      fontSize: "16px",
      fontWeight: "400",
      lineHeight: "21px",
    },
    subtitle2: {
      fontSize: "12px",
      fontWeight: "700",
      lineHeight: "14px",
    },
    button: {
      font: '400 20px "Oswald"',
    },
    link: {
      font: '600 16px "Lato"',
    },
  },
  palette: {
    primary: {
      main: "rgb(92, 6, 140);",
      light: "rgba(0, 0, 0, 0.1)",
      dark: "rgb(92, 6, 140, 0.5)",
      header: "rgba(0, 22, 137, 1)",
      contrastText: "rgba(255, 255, 255, 1)",
    },
    info: {
      main: "rgba(255, 107, 87, 1)",
      contrastText: "rgba(255, 255, 255, 1)",
    },
    link: {
      main: "rgba(92, 6, 140, 1)",
    },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            border: "none", // Убираем рамку с DatePicker
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "Lato",
          fontSize: "12px",
          fontWeight: "700",
          lineHeight: "14px",
          textAlign: "left",
          marginBottom: "9px",
          color: "rgba(0, 0, 0, 0.6)",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: "Lato",
          fontSize: "12px",
          fontWeight: "700",
          lineHeight: "14px",
          textAlign: "left",
          marginBottom: "9px",
          color: "rgba(0, 0, 0, 0.6)",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: "Lato",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "0px 11px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: "30px",
          padding: "0 10px",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: "0",
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          textTransform: "uppercase",
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: "rgba(0, 0, 0, 0.1)",
          "&.Mui-active": {
            color: "rgba(0, 0, 0, 0.5)",
          },
        },
      },
    },
  },
});
const Pages = () => {
  const { instance } = useMsal();

  const isAuthenticated = instance.getActiveAccount() !== null;

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload.account
      ) {
        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         * If you do not want this behavior and prefer your users to stay signed in instead,
         * you can replace the code below with the same pattern used for handling the return from
         * profile edit flow
         */
        if (
          features.enablePasswordReset &&
          compareIssuingPolicy(
            event.payload.idTokenClaims,
            b2cPolicies.names.forgotPassword
          )
        ) {
          let signUpSignInFlowRequest = {
            authority: b2cPolicies.authorities.signUpSignIn.authority,
            scopes: [
              ...protectedResources.api.scopes.read,
              ...protectedResources.api.scopes.write,
            ],
          };
          instance.loginRedirect(signUpSignInFlowRequest);
        }
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (features.enablePasswordReset && event.error && event.error.errorMessage.includes("AADB2C90118")) {
          const resetPasswordRequest = {
            authority: b2cPolicies.authorities.forgotPassword.authority,
            scopes: [],
          };
          instance.loginRedirect(resetPasswordRequest);
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line
  }, [instance]);

  return (
    <Routes>
      <Route path="/" element={isAuthenticated ? <TabPage /> : <HomePage />} />
      <Route path="/invoices" element= {<TabPage tab={3}/>} />
      <Route path="/payments" element= {<TabPage tab={5}/>} />

      <Route path="/member" element={<MemberForm type="edit" />} />
      <Route path="/createMember" element={<MemberForm type="create" />} />
      <Route path="/roleAssignment" element={<ProtectedRoute />}>
        <Route path="/roleAssignment" element={<RoleAssignmentPage />} />
      </Route>
      <Route path="/memberGroups" element={<ProtectedRoute />}>
        <Route path="/memberGroups" element={<MemberGroupsPage />} />
      </Route>
      <Route path="*" element={<RedirectPage/>} />
    </Routes>
  );
};

const App = ({ instance }) => {
  return (
    <MsalProvider instance={instance}>
      <ThemeProvider theme={theme}>
        <PageLayout>
          <Pages />
        </PageLayout>
      </ThemeProvider>
    </MsalProvider>
  );
};
export default App;
